import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { AlertTriangle, Check, Globe, Mail, Megaphone, ShieldAlert, TrendingUp, Users, X } from 'lucide-react';
import { PostBulletin, useBulletins, useTakeDownBulletin } from '../Bulletins.js';
import { api, type SecurityCampaign } from '../../lib/api.js';
import { ago, num } from '../../lib/format.js';
import { Badge, Button, Callout, Panel, Select, Spinner, Tooltip, cn, useToast } from '../../ui/index.js';

/**
 * A starting point for the bulletin about a confirmed campaign: what was
 * seen, by how many people, and what to do. The person posting edits it.
 */
function bulletinFor(c: SecurityCampaign): { title: string; body: string } {
  const what = c.senders[0] ?? c.tickets[0]?.subject ?? c.matched_on.slice(0, 3).join(' ');
  const lines = [
    `**${c.reporters.length} people** have reported this since ${new Date(c.first_seen_at).toLocaleDateString()}.`,
    '',
    ...(c.senders.length ? [`- From: ${c.senders.map((s) => `\`${s}\``).join(', ')}`] : []),
    ...(c.link_domains.length ? [`- Links to: ${c.link_domains.map((d) => `\`${d}\``).join(', ')}`] : []),
    ...(c.senders.length || c.link_domains.length ? [''] : []),
    '**What to do:** do not click links, reply, share passwords or codes, or install anything you are asked to. Delete it or hang up, and report it to the desk.',
    '',
    'If you already did any of those, tell the desk straight away.',
  ];
  return { title: `Security alert${what ? `: ${what}` : ''}`, body: lines.join('\n') };
}

/**
 * Correlated suspicious-message reports (FR-SEC-03).
 *
 * The screen answers one question a per-ticket queue cannot: is this one
 * person's bad morning, or is it happening to everybody? So the headline on
 * every row is the number of PEOPLE, not the number of tickets — six tickets
 * from one person is a stuck Report Phish button, and six people is a
 * campaign, and the ticket count cannot tell you which.
 */

function minutesBetween(a: string, b: string): number {
  return Math.max(0, Math.round((new Date(b).getTime() - new Date(a).getTime()) / 60_000));
}

function span(mins: number): string {
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

export function SecurityTab({ tenant }: { tenant: string }) {
  const [status, setStatus] = useState<'open' | 'confirmed' | 'dismissed' | 'all'>('all');
  const qc = useQueryClient();
  const toast = useToast();

  const q = useQuery({
    queryKey: ['security-campaigns', tenant, status],
    queryFn: () =>
      api.get<{ campaigns: SecurityCampaign[] }>(
        `/api/v1/tenants/${tenant}/security/campaigns?days=1500${
          status === 'all' ? '' : `&status=${status}`
        }`,
      ),
    enabled: Boolean(tenant),
  });

  const judge = useMutation({
    mutationFn: (v: { id: string; status: 'confirmed' | 'dismissed' | 'open' }) =>
      api.patch(`/api/v1/tenants/${tenant}/security/campaigns/${v.id}`, { status: v.status }),
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: ['security-campaigns', tenant] });
      toast.success(
        v.status === 'confirmed'
          ? 'Marked as a real campaign'
          : v.status === 'dismissed'
            ? 'Dismissed as a false alarm'
            : 'Reopened',
      );
    },
    onError: () => toast.error('The campaign could not be updated'),
  });

  const campaigns = q.data?.campaigns ?? [];
  const open = campaigns.filter((c) => c.status === 'open').length;
  const people = campaigns.reduce((n, c) => n + c.reporters.length, 0);

  return (
    <div className="space-y-4">
      <Panel
        title="Correlated reports"
        subtitle="Suspicious-message reports that describe the same thing, grouped into one incident"
        action={
          <Select
            value={status}
            onValueChange={(v) => setStatus(v as typeof status)}
            aria-label="Status filter"
            options={[
              { value: 'all', label: 'All' },
              { value: 'open', label: 'Open' },
              { value: 'confirmed', label: 'Confirmed' },
              { value: 'dismissed', label: 'Dismissed' },
            ]}
          />
        }
        footer={
          <p className="text-2xs leading-relaxed text-ink-tertiary">
            Every count here is of <strong className="text-ink-secondary">people</strong>, never
            of tickets. One person pressing Report Phish six times in two minutes is a stuck
            button; six people pressing it once each is a campaign — and both happen in this
            data. A group is raised at three people who named the same lure, or five who
            reported anything at all inside 90 minutes against a normal hour of one.
          </p>
        }
      >
        {q.isLoading && <Spinner />}
        {q.error && <p className="text-xs text-ink-tertiary">The campaign list could not be loaded.</p>}

        {q.data && campaigns.length === 0 && (
          <p className="py-8 text-center text-xs text-ink-tertiary">
            No correlated reports in this window. Individual reports still route and answer
            normally — this screen only speaks when several describe the same thing.
          </p>
        )}

        {campaigns.length > 0 && (
          <>
            <p className="mb-3 text-2xs text-ink-tertiary">
              {num(campaigns.length)} incident{campaigns.length === 1 ? '' : 's'} · {num(open)} open
              · {num(people)} reporter{people === 1 ? '' : 's'} involved
            </p>
            <ul className="space-y-2.5">
              {campaigns.map((c) => (
                <CampaignRow
                  key={c.id}
                  c={c}
                  tenant={tenant}
                  pending={judge.isPending}
                  onJudge={(s) => judge.mutate({ id: c.id, status: s })}
                />
              ))}
            </ul>
          </>
        )}
      </Panel>
    </div>
  );
}

function CampaignRow({
  c,
  tenant,
  pending,
  onJudge,
}: {
  c: SecurityCampaign;
  tenant: string;
  pending: boolean;
  onJudge: (status: 'confirmed' | 'dismissed' | 'open') => void;
}) {
  const [showAll, setShowAll] = useState(false);
  // The campaign's live bulletin, if it has one: a new one replaces it.
  const live = useBulletins(tenant).data?.bulletins.find((b) => b.campaignId === c.id) ?? null;
  const liveBulletin = live?.title ?? null;
  const takeDown = useTakeDownBulletin(tenant);
  const mins = minutesBetween(c.first_seen_at, c.last_seen_at);
  const big = c.reporters.length >= 8;
  const shown = showAll ? c.tickets : c.tickets.slice(0, 4);

  return (
    <li
      className={cn(
        'rounded-md border p-3',
        c.status === 'dismissed'
          ? 'border-line-subtle bg-surface-sunken/30 opacity-60'
          : big
            ? 'border-danger-line/60 bg-danger-tint/20'
            : 'border-line-subtle bg-surface-sunken/40',
      )}
    >
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="flex items-center gap-1.5 text-sm font-medium text-ink">
          <ShieldAlert aria-hidden className={cn('h-4 w-4', big ? 'text-danger' : 'text-ink-tertiary')} />
          {c.reporters.length} {c.reporters.length === 1 ? 'person' : 'people'} in {span(mins)}
        </span>

        {c.basis === 'content' ? (
          <Tooltip content="These reports name the same sender, link or subject.">
            <span>
              <Badge tone="info" icon={<Mail />}>
                same message
              </Badge>
            </span>
          </Tooltip>
        ) : (
          <Tooltip content="No shared content — this is a spike in how many people reported anything at all.">
            <span>
              <Badge tone="warn" icon={<TrendingUp />}>
                reporting spike
              </Badge>
            </span>
          </Tooltip>
        )}

        <Badge tone="neutral" icon={<Users />}>
          {c.ticket_count} ticket{c.ticket_count === 1 ? '' : 's'}
        </Badge>

        {c.status === 'confirmed' && (
          <Badge tone="danger" icon={<Check />}>
            confirmed
          </Badge>
        )}
        {c.status === 'dismissed' && <Badge tone="neutral">false alarm</Badge>}

        <span className="ml-auto flex items-center gap-1">
          {c.status === 'confirmed' && (
            <PostBulletin
              tenant={tenant}
              campaignId={c.id}
              replaces={liveBulletin}
              initial={bulletinFor(c)}
              trigger={
                <Button variant="ghost" size="sm">
                  <Megaphone aria-hidden className="h-3.5 w-3.5" />
                  {liveBulletin ? 'Replace bulletin' : 'Post a bulletin'}
                </Button>
              }
            />
          )}
          {live && (
            <Button variant="ghost" size="sm" disabled={takeDown.isPending} onClick={() => takeDown.mutate(live.id)}>
              <X aria-hidden className="h-3.5 w-3.5" />
              Take down bulletin
            </Button>
          )}
          {c.status !== 'confirmed' && (
            <Button
              variant="ghost"
              size="sm"
              disabled={pending}
              onClick={() => onJudge('confirmed')}
            >
              <Check aria-hidden className="h-3.5 w-3.5" />
              Real
            </Button>
          )}
          {c.status !== 'dismissed' ? (
            <Button
              variant="ghost"
              size="sm"
              disabled={pending}
              onClick={() => onJudge('dismissed')}
            >
              <X aria-hidden className="h-3.5 w-3.5" />
              False alarm
            </Button>
          ) : (
            <Button variant="ghost" size="sm" disabled={pending} onClick={() => onJudge('open')}>
              Reopen
            </Button>
          )}
        </span>
      </div>

      <p className="mt-1 text-2xs text-ink-tertiary">
        first seen {ago(c.first_seen_at)} · last {ago(c.last_seen_at)}
      </p>

      {c.senders.length > 0 && (
        <p className="mt-2 flex flex-wrap items-center gap-1 text-2xs text-ink-secondary">
          <Mail aria-hidden className="h-3 w-3 text-ink-tertiary" />
          {c.senders.map((s) => (
            <code key={s} className="rounded-xs bg-surface px-1 font-mono">
              {s}
            </code>
          ))}
        </p>
      )}
      {c.link_domains.length > 0 && (
        <p className="mt-1 flex flex-wrap items-center gap-1 text-2xs text-ink-secondary">
          <Globe aria-hidden className="h-3 w-3 text-ink-tertiary" />
          {c.link_domains.map((d) => (
            <code key={d} className="rounded-xs bg-surface px-1 font-mono">
              {d}
            </code>
          ))}
        </p>
      )}

      {c.basis === 'content' && c.matched_on.length > 0 && (
        <p className="mt-2 flex flex-wrap items-center gap-1 text-2xs text-ink-tertiary">
          matched on
          {c.matched_on.slice(0, 8).map((t) => (
            <Badge key={t} tone="neutral">
              {t}
            </Badge>
          ))}
        </p>
      )}

      {c.basis === 'rate' && (
        <Callout tone="warn" className="mt-2 py-1.5">
          Nothing links these reports but their timing. That is the finding: several people
          reported something in the same short window, and what they saw is not recorded
          anywhere the desk can compare.
        </Callout>
      )}

      <ul className="mt-2 space-y-0.5">
        {shown.map((t) => (
          <li key={t.id} className="truncate text-2xs">
            <Link
              to={`/t/${tenant}/${t.number}`}
              className="text-ink-secondary underline-offset-2 hover:underline"
            >
              #{t.number}
            </Link>{' '}
            <span className="text-ink-tertiary">{t.subject}</span>
          </li>
        ))}
      </ul>
      {c.tickets.length > 4 && (
        <button
          type="button"
          onClick={() => setShowAll((v) => !v)}
          className="mt-1 text-2xs text-ink-tertiary underline-offset-2 hover:underline"
        >
          {showAll ? 'Show fewer' : `Show all ${c.tickets.length}`}
        </button>
      )}

      {big && c.status === 'open' && (
        <Callout tone="danger" icon={<AlertTriangle />} className="mt-2 py-1.5">
          {c.reporters.length} people reported this inside {span(mins)}. Anyone who did not report
          it also received it.
        </Callout>
      )}
    </li>
  );
}
